import { AdminLockAccountRequestDto, UserDetailDto } from '@ivy-monorepo/shared'
import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { adminService } from '../../services/admin.service'

type DurationPreset = 'default' | '60' | '360' | '1440' | '10080' | 'permanent' | 'custom'

interface LockUserModalProps {
  isOpen: boolean
  onClose: () => void
  publicId: string
  updateUser: (userData: UserDetailDto) => void
}

const LockUserModal = ({ isOpen, onClose, publicId, updateUser }: LockUserModalProps) => {
  const [reason, setReason] = useState('')
  const [durationPreset, setDurationPreset] = useState<DurationPreset>('default')
  const [customMinutes, setCustomMinutes] = useState('')
  const [isLockPending, setIsLockPending] = useState(false)
  const [lockError, setLockError] = useState<string | null>(null)

  const handleClose = () => {
    if (isLockPending) return
    onClose()
  }

  const buildDto = (): AdminLockAccountRequestDto | null => {
    const trimmed = reason.trim()
    if (!trimmed) {
      return null
    }

    if (durationPreset === 'permanent') {
      return { reason: trimmed, permanentUntilManualUnlock: true }
    }

    if (durationPreset === 'custom') {
      const n = Number.parseInt(customMinutes, 10)
      if (!Number.isFinite(n) || n < 1) {
        return null
      }
      return { reason: trimmed, lockDurationMinutes: n }
    }

    if (durationPreset === 'default') {
      return { reason: trimmed }
    }

    return { reason: trimmed, lockDurationMinutes: Number.parseInt(durationPreset, 10) }
  }

  const handleLock = async () => {
    if (!publicId) return
    const dto = buildDto()
    if (!dto) {
      setLockError(durationPreset === 'custom' ? 'Enter a valid number of minutes (at least 1).' : 'Reason is required.')
      return
    }

    try {
      setIsLockPending(true)
      setLockError(null)
      const updated = await adminService.lockUser(publicId, dto)
      updateUser(updated)
      onClose()
    } catch (err) {
      console.log('Failed to lock user', err)
      setLockError('Failed to lock user.')
    } finally {
      setIsLockPending(false)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setReason('')
    setDurationPreset('default')
    setCustomMinutes('')
    setLockError(null)
  }, [isOpen])

  const canSubmit =
    reason.trim().length > 0 &&
    (durationPreset !== 'custom' ||
      (() => {
        const n = Number.parseInt(customMinutes, 10)
        return Number.isFinite(n) && n >= 1
      })())

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>Lock user</h2>
        <p style={{ marginTop: 0, opacity: 0.85, fontSize: '0.95rem' }}>
          The user cannot sign in until the lock expires or an admin unlocks the account.
        </p>
        <form className="modal-body-content">
          <div className="form-group">
            <label>Reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} disabled={isLockPending} />
          </div>
          <div className="form-group">
            <label>Lock duration</label>
            <select value={durationPreset} onChange={(e) => setDurationPreset(e.target.value as DurationPreset)} disabled={isLockPending}>
              <option value="60">1 hour</option>
              <option value="360">6 hours</option>
              <option value="1440">24 hours</option>
              <option value="10080">7 days</option>
              <option value="permanent">Until manually unlocked</option>
              <option value="custom">Custom (minutes)</option>
            </select>
          </div>
          {durationPreset === 'custom' && (
            <div className="form-group">
              <label>Minutes</label>
              <input
                type="number"
                min={1}
                value={customMinutes}
                onChange={(e) => setCustomMinutes(e.target.value)}
                disabled={isLockPending}
              />
            </div>
          )}
        </form>
        {lockError && (
          <div className="page-error" style={{ margin: 0 }}>
            {lockError}
          </div>
        )}
        <div className="modal-footer-content">
          <button type="button" className="styled-button" onClick={handleClose} disabled={isLockPending}>
            Cancel
          </button>
          <button type="button" className="styled-button" onClick={handleLock} disabled={isLockPending || !canSubmit}>
            Lock
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default LockUserModal
